import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, MagnifyingGlass } from "@phosphor-icons/react";

// Admin-only BDM ↔ BDE team assignment. Reuses users.reporting_manager_id
// (no parallel mapping system) via /api/admin/team/* endpoints.
export const ManageTeamModal = ({ bdm, onClose }) => {
  const [candidates, setCandidates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState("");

  const fetchCandidates = useCallback(async () => {
    try {
      const res = await api.get(`/admin/team/bdm-candidates`);
      setCandidates(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load team candidates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCandidates(); }, [fetchCandidates]);

  const toggleBde = async (bde) => {
    const isAssignedHere = bde.current_bdm_id === bdm.bdm_user_id;
    setBusyId(bde.user_id);
    try {
      if (isAssignedHere) {
        await api.put(`/admin/team/unassign`, { bde_user_id: bde.user_id });
        toast.success(`${bde.name} removed from ${bdm.bdm_name}`);
      } else {
        await api.put(`/admin/team/assign`, { bde_user_id: bde.user_id, bdm_user_id: bdm.bdm_user_id });
        toast.success(`${bde.name} assigned to ${bdm.bdm_name}`);
      }
      fetchCandidates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update assignment");
    } finally {
      setBusyId("");
    }
  };

  const bdes = (candidates?.bdes || []).filter((b) =>
    !search || b.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div data-testid="manage-team-modal" className="bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto border border-slate-200 shadow-2xl">
        <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#002FA7] font-bold">Manage Team</div>
            <h3 className="text-xl font-semibold" style={{fontFamily: 'Outfit, sans-serif'}}>BDM: {bdm.bdm_name}</h3>
          </div>
          <button data-testid="manage-team-close" onClick={onClose} className="p-2 hover:bg-slate-100"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="manage-team-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search BDE by name..."
              className="w-full pl-9 pr-3 py-2.5 border border-slate-300 rounded-none text-sm outline-none" />
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><div className="loading-spinner"></div></div>
          ) : (
            <div className="space-y-1">
              {bdes.length === 0 && <div className="text-sm text-slate-500 py-6 text-center">No BDE users found</div>}
              {bdes.map((b) => {
                const assignedHere = b.current_bdm_id === bdm.bdm_user_id;
                const assignedElsewhere = b.current_bdm_id && !assignedHere;
                return (
                  <label key={b.user_id} data-testid={`manage-team-bde-row-${b.user_id}`}
                    className="flex items-center justify-between p-3 border border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <div className="flex items-center gap-3">
                      <input type="checkbox" data-testid={`manage-team-checkbox-${b.user_id}`}
                        checked={assignedHere} disabled={busyId === b.user_id}
                        onChange={() => toggleBde(b)} className="w-4 h-4 accent-[#002FA7]" />
                      <div>
                        <div className="text-sm font-medium">{b.name}</div>
                        <div className="text-xs text-slate-500">{b.email}</div>
                      </div>
                    </div>
                    {assignedElsewhere && (
                      <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1">
                        With {b.current_bdm_name}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageTeamModal;
